import * as React from "react";
import Popover from "@mui/material/Popover";
import toast from "react-hot-toast";
import TeamFormDrawer from "./TeamFormDrawer";
import { fetchTeamMemberCustomers, fetchTeams, TeamStatusFilter, toggleTeamDeleted } from "./team.service";
import { Customer, TeamRow } from "./team.types";
import SearchInput from "../../common/SearchInput";

const PAGE_SIZE = 10;

export default function ManageTeamCustomerTable() {

  const [rows, setRows] = React.useState<any[]>([]);
  const [customerList, setCustomerList] = React.useState<Customer[]>([]);
  const [loading, setLoading] = React.useState(false);

  const [searchQuery, setSearchQuery] = React.useState("");
  const [currentPage, setCurrentPage] = React.useState(0);

  const [formOpen, setFormOpen] = React.useState(false);
  const [editId, setEditId] = React.useState<string | null>(null);
  const [statusFilter, setStatusFilter] = React.useState<TeamStatusFilter>("all");
  const [popover, setPopover] = React.useState<{ anchor: HTMLElement; content: React.ReactNode } | null>(null);

  const getTeams = React.useCallback(async (status: TeamStatusFilter) => {
    setLoading(true);
    try {
      setRows(await fetchTeams(status));
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    getTeams(statusFilter);
  }, [statusFilter, getTeams]);

  React.useEffect(() => {
    (async () => setCustomerList(await fetchTeamMemberCustomers()))();
  }, []);

  React.useEffect(() => {
    setCurrentPage(0);
  }, [searchQuery, statusFilter]);
  const mappedRows: TeamRow[] = React.useMemo(
    () =>
      rows.map((row: any) => ({
        ...row,
        id: row?._id || row.id,
        _id: row?._id || row.id,
        teamName: row?.teamName || "",
        description: row?.description || "",
        teamLeaders: row?.teamLeaders || [],
        teamMembers: row?.teamMembers || [],
        isDeleted: !!row?.isDeleted,
      })),
    [rows]
  );

  const filteredRows = React.useMemo(() => {
    const s = searchQuery.toLowerCase();
    if (!s) return mappedRows;
    return mappedRows.filter((row) => {
      const leaderNames = row.teamLeaders.map((l: any) => l?.name || "").join(" ").toLowerCase();
      const membersCount = (row.teamMembers?.length || 0).toString();
      return (
        row.teamName.toLowerCase().includes(s) ||
        row.description.toLowerCase().includes(s) ||
        leaderNames.includes(s) ||
        membersCount.includes(s)
      );
    });
  }, [mappedRows, searchQuery]);

  const pageCount = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const pagedRows = filteredRows.slice(currentPage * PAGE_SIZE, currentPage * PAGE_SIZE + PAGE_SIZE);

  const openAdd = () => {
    setEditId(null);
    setFormOpen(true);
  };
  const openEdit = (row: TeamRow) => {
    setEditId(row.id);
    setFormOpen(true);
  };

  const handleToggleStatus = async (row: TeamRow) => {
    const nextDeleted = !row.isDeleted;
    try {
      const response = await toggleTeamDeleted(row.id, nextDeleted);
      if (response.status === 200) {
        toast.success(nextDeleted ? "Customer team deactivated successfully." : "Customer team activated successfully.");
        getTeams(statusFilter);
      } else {
        toast.error("Error updating customer team status.");
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.error || "An error occurred while updating customer team status.");
    }
  };
  // Leaders come as objects with names from list API; resolve fallback via customerList
  const leaderNames = (leaders: any[]): string[] => {
    const names = leaders.map((l) => l?.name).filter(Boolean);
    if (names.length) return names;
    const ids = leaders.map((l) => l?._id || l);
    return customerList.filter((c) => ids.includes(c._id)).map((c) => c.name);
  };

  const showPopover = (e: React.MouseEvent<HTMLElement>, title: string, names: string[]) => {
    setPopover({
      anchor: e.currentTarget,
      content: (
        <>
          <div className="ctpl-popover-title">{title} ({names.length})</div>
          <ul className="ctpl-popover-list">
            {names.length ? names.map((n, i) => <li key={i}>{n}</li>) : <li>None</li>}
          </ul>
        </>
      ),
    });
  };

  return (
    <>
      <div className="ctpl-card">
        <div className="ctpl-toolbar">
          <SearchInput
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search teams…"
          />

          <div className="ctpl-filter-chips">
            {(["all", "active", "inactive"] as const).map((f) => (
              <button
                key={f}
                type="button"
                className={`ctpl-filter-chip${statusFilter === f ? " active" : ""}`}
                onClick={() => setStatusFilter(f)}
              >
                {f === "all" ? "All" : f === "active" ? "Active" : "Inactive"}
              </button>
            ))}
          </div>

          <div className="ctpl-spacer" />

          <button className="ctpl-btn ctpl-btn-primary" onClick={openAdd}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
              <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Create New Team
          </button>
        </div>

        <div className="ctpl-table-scroll">
          <table className="ctpl-table">
            <thead>
              <tr>
                {/* <th style={{ width: "28%" }}>S. No.</th> */}
                <th style={{ width: "20%" }}>Team</th>
                <th style={{ width: "25%" }}>Description</th>
                <th style={{ width: "20%" }}>Leaders</th>
                <th style={{ width: "20%" }}>Members</th>
                <th style={{ width: "15%", textAlign: "right", paddingRight: 22 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td className="ctpl-empty-cell" colSpan={5}>Loading teams…</td></tr>
              ) : pagedRows.length === 0 ? (
                <tr><td className="ctpl-empty-cell" colSpan={5}>No teams found</td></tr>
              ) : (
                pagedRows.map((row) => (
                  <tr key={row.id}>

                    <td>
                      <div className="ctpl-tmpl-name">{row.teamName}</div>
                    </td>
                    <td>
                      <div className="ctpl-tmpl-sub" style={{ maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {row.description || "—"}
                      </div>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="ctpl-channel-badge email"
                        onClick={(e) =>
                          showPopover(e, "👑 Team leaders", (row.teamLeaders || []).map((m: any) => m?.name || "").filter(Boolean))
                        }
                      >
                        👑 {row.teamLeaders?.length || 0}
                      </button>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="ctpl-channel-badge email"
                        onClick={(e) =>
                          showPopover(e, "👥 Team members", (row.teamMembers || []).map((m: any) => m?.name || "").filter(Boolean))
                        }
                      >
                        👥 {row.teamMembers?.length || 0}
                      </button>
                    </td>
                    {/* <td>
                      <span className={`ctpl-status-pill ${!row.isDeleted ? "active" : "inactive"}`}>
                        <span className="ctpl-status-dot" />
                        {!row.isDeleted ? "Active" : "Inactive"}
                      </span>
                    </td> */}
                    <td>
                      <div className="ctpl-row-actions">
                        <button className="ctpl-action-circle edit" title={row.isDeleted ? "Activate to edit" : "Edit"} onClick={() => openEdit(row)} disabled={row.isDeleted}>
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
                          </svg>
                        </button>
                        <button
                          type="button"
                          className={`ctpl-switch${!row.isDeleted ? " on" : ""}`}
                          title={!row.isDeleted ? "Deactivate" : "Activate"}
                          onClick={() => handleToggleStatus(row)}
                        />
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="ctpl-table-footer">
          <span>{filteredRows.length} team{filteredRows.length === 1 ? "" : "s"}</span>
          <div className="ctpl-pagination">
            <button className="ctpl-page-btn" disabled={currentPage === 0} onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}>‹</button>
            <span>Page {currentPage + 1} of {pageCount}</span>
            <button className="ctpl-page-btn" disabled={currentPage >= pageCount - 1} onClick={() => setCurrentPage((p) => Math.min(pageCount - 1, p + 1))}>›</button>
          </div>
        </div>
      </div>

      <TeamFormDrawer
        open={formOpen}
        userId={editId}
        customerList={customerList}
        onClose={() => setFormOpen(false)}
        onSaved={() => getTeams(statusFilter)}
      />
      <Popover
        open={!!popover}
        anchorEl={popover?.anchor}
        onClose={() => setPopover(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        PaperProps={{ className: "ctpl-popover-paper" }}
      >
        {popover?.content}
      </Popover>
    </>
  );
}